import { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiGithub, FiExternalLink } from 'react-icons/fi';

const projects = [
  {
    num: '01',
    status: 'LIVE',
    statusColor: '#22c55e',
    accent: 'linear-gradient(135deg, #3b82f6, #6366f1)',
    title: 'School Management System',
    client: 'Local Childcare Centre, Nepal',
    problem: 'A family-run childcare centre in Nepal was managing student records, attendance, and staff entirely on paper — slow, error-prone, and unscalable.',
    solution: 'Built a full-stack MERN platform with role-based access, student enrollment, live attendance tracking, staff management, and automated reporting.',
    impact: 'Currently live in production — eliminated manual paperwork entirely and cut administrative time by an estimated 60%.',
    tech: ['MongoDB', 'Express.js', 'React', 'Node.js', 'Tailwind CSS', 'JWT Auth'],
    github: 'https://github.com/arbhram',
    live: 'https://sms-nepal.vercel.app',
  },
  {
    num: '02',
    status: 'IN PROGRESS',
    statusColor: '#f59e0b',
    accent: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
    title: 'EV Station Finder',
    client: 'Personal Project',
    problem: 'EV drivers in unfamiliar areas struggle to find available charging stations in real time, leading to range anxiety and poor trip planning.',
    solution: 'Building a web app integrating live mapping APIs to surface nearby EV chargers, filter by connector type, and show live availability.',
    impact: 'In active development — focused on sub-2s load times, mobile-first UX, and accurate real-time data from charging networks.',
    tech: ['React', 'Node.js', 'Express.js', 'Mapping API', 'Tailwind CSS'],
    github: 'https://github.com/arbhram',
    live: null,
  },
];

function ProjectCard({ project, index }) {
  const cardRef = useRef(null);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.08 });

  const onMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 12;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -12;
    cardRef.current.style.transform = `perspective(1200px) rotateX(${y}deg) rotateY(${x}deg) translateZ(8px)`;
  };
  const onMouseLeave = () => { cardRef.current.style.transform = 'perspective(1200px) rotateX(0deg) rotateY(0deg) translateZ(0px)'; };

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 48 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      style={{ transformStyle: 'preserve-3d' }}>
      <div ref={cardRef} onMouseMove={onMouseMove}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; e.currentTarget.style.boxShadow = '0 24px 64px rgba(0,0,0,0.35)'; }}
        onMouseLeave={e => { onMouseLeave(); e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.boxShadow = 'none'; }}
        style={{
          background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.08)', borderRadius: '22px',
          overflow: 'hidden', transition: 'transform 0.4s cubic-bezier(0.16,1,0.3,1), border-color 0.3s, box-shadow 0.3s',
          transformStyle: 'preserve-3d', height: '100%', display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Gradient accent bar */}
        <div style={{ height: 3, background: project.accent }} />

        <div style={{ padding: '1.75rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', color: '#475569' }}>{project.num}</span>
            <span style={{
              fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem',
              color: project.statusColor, background: `${project.statusColor}15`,
              border: `1px solid ${project.statusColor}30`,
              borderRadius: '999px', padding: '0.2rem 0.7rem', letterSpacing: '0.06em',
            }}>
              {project.status}
            </span>
          </div>

          <h3 style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '1.2rem', fontWeight: 700, color: '#f0f6ff', marginBottom: '0.3rem', letterSpacing: '-0.01em' }}>
            {project.title}
          </h3>
          <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', color: '#475569', marginBottom: '1.5rem' }}>
            {project.client}
          </p>

          {/* Case study grid */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem', flex: 1 }}>
            {[
              { label: 'Problem', text: project.problem, color: '#f87171' },
              { label: 'Solution', text: project.solution, color: '#60a5fa' },
              { label: 'Impact', text: project.impact, color: '#34d399' },
            ].map(({ label, text, color }) => (
              <div key={label} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '10px', padding: '0.85rem 1rem' }}>
                <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem', color, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>{label}</p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.65 }}>{text}</p>
              </div>
            ))}
          </div>

          {/* Tech pills */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginBottom: '1.4rem' }}>
            {project.tech.map(t => (
              <span key={t} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.66rem', color: '#a5b4fc', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.18)', borderRadius: '6px', padding: '0.22rem 0.6rem' }}>{t}</span>
            ))}
          </div>

          {/* Links */}
          <div style={{ display: 'flex', gap: '1rem' }}>
            <a href={project.github} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#64748b', textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#f0f6ff')} onMouseLeave={e => (e.currentTarget.style.color = '#64748b')}>
              <FiGithub size={14} /> Code
            </a>
            {project.live && (
              <a href={project.live} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#64748b', textDecoration: 'none', transition: 'color 0.2s' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#60a5fa')} onMouseLeave={e => (e.currentTarget.style.color = '#64748b')}>
                <FiExternalLink size={14} /> Live Demo
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Projects() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.05 });
  return (
    <section id="projects" style={{ padding: '120px 0' }}>
      <div className="section-container">
        <motion.p ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className="section-label">Projects</motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.1 }} className="section-title" style={{ marginBottom: '0.75rem' }}>
          Case{' '}
          <span style={{ background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Studies</span>
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.2 }} style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '4rem', maxWidth: '480px', lineHeight: 1.7 }}>
          Real problems. Real solutions. Every project is broken down by problem, solution, and measurable impact.
        </motion.p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem' }}>
          {projects.map((p, i) => <ProjectCard key={p.num} project={p} index={i} />)}
        </div>
      </div>
    </section>
  );
}
